import { useEffect } from "react";
import { useCustom } from "@refinedev/core";
import { Form, Input, Modal, Select } from "antd";
import { leagueOptions } from "pages/Video/leagueOptions";
import {
  MedicineContestSeason,
  MedicineContestTeam,
  MedicineContestWinner,
  MedicineContestWinnerFormValues,
} from "./types";

type WinnerModalProps = {
  open: boolean;
  contestId: number;
  saving: boolean;
  winner?: MedicineContestWinner;
  usedPlacesByLeague: Record<number, number[]>;
  onCancel: () => void;
  onSubmit: (values: MedicineContestWinnerFormValues) => void;
};

const placeOptions = [1, 2, 3].map((place) => ({
  label: `${place} место`,
  value: place,
}));

export const WinnerModal = ({
  open,
  contestId,
  saving,
  winner,
  usedPlacesByLeague,
  onCancel,
  onSubmit,
}: WinnerModalProps) => {
  const [form] = Form.useForm<MedicineContestWinnerFormValues>();
  const leagueId = Form.useWatch("leagueId", form);
  const seasonId = Form.useWatch("seasonId", form);

  const { data: seasonsData, isLoading: seasonsLoading } = useCustom<
    MedicineContestSeason[]
  >({
    dataProviderName: "medicineContestsDataProvider",
    url: "seasons",
    method: "get",
    meta: { leagueId },
    queryOptions: {
      enabled: Boolean(leagueId),
    },
  });

  const { data: teamsData, isLoading: teamsLoading } = useCustom<
    MedicineContestTeam[]
  >({
    dataProviderName: "medicineContestsDataProvider",
    url: "teams",
    method: "get",
    meta: { leagueId, seasonId },
    queryOptions: {
      enabled: Boolean(leagueId) && Boolean(seasonId),
    },
  });

  const seasons = seasonsData?.data || [];
  const teams = teamsData?.data || [];

  const teamOptions = teams.map((team) => ({
    label: team.city ? `${team.title} (${team.city})` : team.title,
    value: team.teamId,
  }));

  // На редактировании команда победителя может быть из другого (не текущего)
  // сезона и отсутствовать в загруженном списке — добавляем её опцией, чтобы
  // показывалось название, а не сырой teamId.
  if (
    winner?.teamId != null &&
    !teamOptions.some((option) => option.value === winner.teamId)
  ) {
    teamOptions.unshift({
      label: winner.clubName || winner.teamSlug || String(winner.teamId),
      value: winner.teamId,
    });
  }

  const usedPlaces = leagueId ? usedPlacesByLeague[leagueId] || [] : [];
  const availablePlaceOptions = placeOptions.map((option) => ({
    ...option,
    disabled:
      !leagueId ||
      (usedPlaces.includes(option.value) &&
        !(option.value === winner?.place && leagueId === winner?.leagueId)),
  }));

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      id: winner?.id,
      contestId,
      place: winner?.place,
      leagueId: winner?.leagueId,
      seasonId: undefined,
      teamId: winner?.teamId,
    });
  }, [contestId, form, open, winner]);

  // Сезон у победителя не хранится. При редактировании подставляем последний
  // сезон лиги, чтобы форма была валидна и список команд подгрузился;
  // при добавлении сезон выбирается вручную.
  useEffect(() => {
    if (!open || !winner || !seasons.length || seasonId) {
      return;
    }

    form.setFieldValue("seasonId", seasons[0].seasonId);
  }, [open, winner, seasons, seasonId, form]);

  const handleLeagueChange = () => {
    form.setFieldValue("seasonId", undefined);
    form.setFieldValue("place", undefined);
    form.setFieldValue("teamId", undefined);
  };

  const handleSeasonChange = () => {
    form.setFieldValue("teamId", undefined);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSubmit(values);
  };

  return (
    <Modal
      title={winner ? "Редактировать победителя" : "Добавить победителя"}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<MedicineContestWinnerFormValues> form={form} layout="vertical">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="contestId" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="leagueId"
          label="Лига"
          rules={[
            {
              required: true,
              message: "Выберите лигу",
            },
          ]}
        >
          <Select
            options={leagueOptions}
            placeholder="Выберите лигу"
            onChange={handleLeagueChange}
          />
        </Form.Item>

        <Form.Item
          name="seasonId"
          label="Сезон"
          rules={[
            {
              required: true,
              message: "Выберите сезон",
            },
          ]}
        >
          <Select
            showSearch
            disabled={!leagueId}
            loading={seasonsLoading}
            placeholder={leagueId ? "Выберите сезон" : "Сначала выберите лигу"}
            optionFilterProp="label"
            onChange={handleSeasonChange}
            options={seasons.map((season) => ({
              label: season.title,
              value: season.seasonId,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="place"
          label="Место"
          rules={[
            {
              required: true,
              message: "Выберите место",
            },
          ]}
        >
          <Select
            disabled={!seasonId}
            options={availablePlaceOptions}
            placeholder={seasonId ? "Выберите место" : "Сначала выберите сезон"}
          />
        </Form.Item>

        <Form.Item
          name="teamId"
          label="Команда"
          rules={[
            {
              required: true,
              message: "Выберите команду",
            },
          ]}
        >
          <Select
            showSearch
            disabled={!seasonId}
            loading={teamsLoading}
            placeholder={
              seasonId ? "Выберите команду" : "Сначала выберите сезон"
            }
            optionFilterProp="label"
            options={teamOptions}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
